import React, { useState } from 'react'
import { Input, message } from 'antd'
import Tesseract from 'tesseract.js'

const { TextArea } = Input

/**
 * 支持OCR图片识别的TextArea组件
 * @param {Object} props - 组件属性
 * @param {string} props.value - 输入值
 * @param {function} props.onChange - 值变化回调
 * @param {string} props.placeholder - 占位符
 * @param {boolean} props.disabled - 是否禁用
 * @param {Object} props.autoSize - 自动调整大小配置
 * @param {string} props.extra - 额外提示信息
 * @param {string} props.language - OCR识别语言，默认为'chi_sim'（中文简体）
 * @param {Object} props.style - 样式对象
 * @param {string} props.className - CSS类名
 * @param {function} props.onPaste - 粘贴事件回调（可选，会与OCR功能合并）
 * @param {Object} props.textAreaProps - 传递给TextArea的其他属性
 */
function OCRTextArea({
    value,
    onChange,
    placeholder = "请输入内容，或直接粘贴图片进行文字识别...",
    disabled = false,
    autoSize = { minRows: 1, maxRows: 6 },
    extra = "提示：可以直接粘贴图片，系统会自动识别图片中的文字并填充到文本框中",
    language = 'chi_sim', // 默认中文简体
    style,
    className,
    onPaste,
    textAreaProps = {},
    ...restProps
}) {
    const [isProcessingOCR, setIsProcessingOCR] = useState(false)

    // OCR文字识别函数
    const processImageWithOCR = async (imageFile) => {
        let loadingMessage = null
        try {
            setIsProcessingOCR(true)
            loadingMessage = message.loading('正在识别图片中的文字...', 0)
            
            console.log('开始OCR识别，语言:', language, '文件:', imageFile.name, imageFile.size, imageFile.type)
            
            const { data: { text } } = await Tesseract.recognize(
                imageFile,
                language, // 使用传入的语言配置
                {
                    logger: m => {
                        console.log('OCR进度:', m.status, Math.round(m.progress * 100) + '%')
                        if (m.status === 'recognizing text') {
                            loadingMessage()
                            loadingMessage = message.loading(`识别进度: ${Math.round(m.progress * 100)}%`, 0)
                        }
                    }
                }
            )
            
            if (loadingMessage) {
                loadingMessage()
            }
            
            // 清理OCR识别结果中的多余空格
            const result = text
                .trim() // 去除首尾空格
                .replace(/\s+/g, ' ') // 将多个连续空格替换为单个空格
                .replace(/\n\s+/g, '\n') // 去除换行后的多余空格
                .replace(/\s+\n/g, '\n') // 去除换行前的多余空格
                .replace(/\n{3,}/g, '\n\n') // 将多个连续换行替换为最多两个换行
                // 处理中文字符间的多余空格
                .replace(/([\u4e00-\u9fa5])\s+([\u4e00-\u9fa5])/g, '$1$2') // 去除中文字符间的空格
                .replace(/([\u4e00-\u9fa5])\s+([\u4e00-\u9fa5])/g, '$1$2') // 再次处理，确保清理干净
                // 处理数字和中文之间的空格
                .replace(/([\u4e00-\u9fa5])\s+(\d)/g, '$1$2') // 中文和数字之间
                .replace(/(\d)\s+([\u4e00-\u9fa5])/g, '$1$2') // 数字和中文之间
                // 处理标点符号周围的空格
                .replace(/\s+([，。！？；：])/g, '$1') // 标点符号前的空格
                .replace(/([，。！？；：])\s+/g, '$1') // 标点符号后的空格
                .trim() // 再次去除首尾空格
            
            console.log('OCR识别结果（清理前）:', text)
            console.log('OCR识别结果（清理后）:', result)
            return result
        } catch (error) {
            if (loadingMessage) {
                loadingMessage()
            }
            console.error('OCR错误详情:', error)
            message.error('图片文字识别失败: ' + error.message)
            return null
        } finally {
            setIsProcessingOCR(false)
        }
    }

    // 处理粘贴事件
    const handlePaste = async (e) => {
        // 先调用用户自定义的onPaste回调
        if (onPaste) {
            onPaste(e)
        }

        const items = e.clipboardData?.items
        if (!items) return

        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.type.indexOf('image') !== -1) {
                e.preventDefault()
                const file = item.getAsFile()
                if (file) {
                    console.log('检测到图片文件:', file.name, file.size, file.type)
                    try {
                        const recognizedText = await processImageWithOCR(file)
                        if (recognizedText && recognizedText.length > 0) {
                            // 获取当前值并追加识别出的文字
                            const currentValue = value || ''
                            const newValue = currentValue + (currentValue ? '\n' : '') + recognizedText
                            
                            // 调用onChange回调
                            if (onChange) {
                                onChange({
                                    target: {
                                        value: newValue
                                    }
                                })
                            }
                            
                            message.success('图片文字识别完成并已填充到文本框中')
                        } else {
                            message.warning('未能识别出图片中的文字，请尝试更清晰的图片')
                        }
                    } catch (error) {
                        console.error('OCR处理错误:', error)
                        message.error('图片处理失败: ' + error.message)
                    }
                }
                break
            }
        }
    }

    return (
        <TextArea
            value={value}
            onChange={onChange}
            onPaste={handlePaste}
            placeholder={placeholder}
            disabled={disabled || isProcessingOCR}
            autoSize={autoSize}
            style={style}
            className={className}
            {...textAreaProps}
            {...restProps}
        />
    )
}

export default OCRTextArea
